/**
 * tenantStorage — Iter 257.n
 *
 * Storage facade that namespaces every key by the authenticated user
 * + org so a single browser shared between multiple operators can't
 * leak per-tenant data via session/local storage. Created in response
 * to the production cross-tenant calendar leak (Iter 257.m) where the
 * `events` array was hydrated from an unscoped sessionStorage key on
 * first paint after a logout → login cycle.
 *
 * Usage:
 *   import { tStorage, setTenantStorageContext } from '@/lib/tenantStorage';
 *   // App.js calls setTenantStorageContext(user) on auth changes.
 *
 *   tStorage.local.set('events_saved_views', JSON.stringify(views));
 *   const raw = tStorage.local.get('events_saved_views');
 *
 *   // Per-user (defaults). Use `{scope:'org'}` to share across users
 *   // in the same org, or `{scope:'global'}` for explicitly non-
 *   // tenant data (theme, timezone, sidebar-collapsed prefs).
 *   tStorage.session.set('foo', 'bar', { scope: 'org' });
 *   tStorage.local.get('theme', { scope: 'global' });
 */

let _ctx = { user_id: 'anon', org_id: 'anon' };

export const setTenantStorageContext = (user) => {
  _ctx = {
    user_id: user?.user_id || 'anon',
    org_id: user?.org_id || 'anon',
  };
};

const prefix = (scope = 'user') => {
  if (scope === 'global') return 'tnt.g.';
  if (scope === 'org') return `tnt.o.${_ctx.org_id}.`;
  return `tnt.u.${_ctx.user_id}.`;
};

const wrap = (storage) => ({
  set(key, value, opts = {}) {
    try { storage.setItem(prefix(opts.scope) + key, value); } catch { /* quota / private mode */ }
  },
  get(key, opts = {}) {
    try { return storage.getItem(prefix(opts.scope) + key); } catch { return null; }
  },
  remove(key, opts = {}) {
    try { storage.removeItem(prefix(opts.scope) + key); } catch { /* noop */ }
  },
});

export const tStorage = {
  local: typeof window !== 'undefined' ? wrap(window.localStorage) : { set() {}, get() { return null; }, remove() {} },
  session: typeof window !== 'undefined' ? wrap(window.sessionStorage) : { set() {}, get() { return null; }, remove() {} },
};

/**
 * Sweep all per-user / per-org tenant-scoped keys for the CURRENT
 * context (i.e. before the user logs out). The global App.js logout()
 * sweeps a hard-coded list of legacy prefixes; this helper covers
 * everything written through tStorage going forward.
 */
export const purgeTenantStorage = () => {
  try {
    const userPrefix = `tnt.u.${_ctx.user_id}.`;
    const orgPrefix = `tnt.o.${_ctx.org_id}.`;
    for (const storage of [window.localStorage, window.sessionStorage]) {
      const drop = [];
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (k && (k.startsWith(userPrefix) || k.startsWith(orgPrefix))) drop.push(k);
      }
      drop.forEach((k) => storage.removeItem(k));
    }
  } catch { /* noop */ }
};
